import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/* Planner auto-refresh control.
   ---------------------------------------------------------------------------
   Shows a live countdown ("⟳ 2:45") and fires a SOFT refresh every
   ``intervalSec`` seconds. Clicking the control refreshes immediately and
   resets the countdown. The timer PAUSES while any dialog / overlay is open
   (``paused`` prop) so an in-progress edit is never disrupted, resuming on
   close. The per-second re-render is isolated to this small component so the
   larger planner tree isn't re-rendered every tick. */
export default function PlannerAutoRefresh({ intervalSec = 180, paused = false, onRefresh }) {
  const [secs, setSecs] = useState(intervalSec);
  const [spinning, setSpinning] = useState(false);
  const pausedRef = useRef(paused);
  const onRefreshRef = useRef(onRefresh);
  const spinTimer = useRef(null);

  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  const doRefresh = useCallback(() => {
    setSpinning(true);
    try { onRefreshRef.current && onRefreshRef.current(); } catch { /* noop */ }
    if (spinTimer.current) clearTimeout(spinTimer.current);
    spinTimer.current = setTimeout(() => setSpinning(false), 600);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current) return; // frozen while a dialog/overlay is open
      setSecs((s) => {
        if (s <= 1) { doRefresh(); return intervalSec; }
        return s - 1;
      });
    }, 1000);
    return () => { clearInterval(id); if (spinTimer.current) clearTimeout(spinTimer.current); };
  }, [intervalSec, doRefresh]);

  const manual = () => { setSecs(intervalSec); doRefresh(); };

  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, '0');

  return (
    <button
      type="button"
      onClick={manual}
      data-testid="planner-refresh-button"
      aria-label="Refresh planner now"
      title={paused
        ? 'Auto-refresh paused while a dialog is open · click to refresh now'
        : 'Refresh now · auto-refreshes every 3 minutes'}
      className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[12px] font-medium text-[var(--n-muted)] transition-colors hover:border-[var(--n-accent)] hover:text-[var(--n-ink)]"
    >
      <RefreshCw size={14} className={spinning ? 'animate-spin text-[var(--n-accent)]' : ''} />
      <span className="hidden tabular-nums sm:inline" data-testid="planner-refresh-countdown">
        {paused ? 'Paused' : `${mm}:${ss}`}
      </span>
    </button>
  );
}
